import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Briefcase,
  Code,
  Terminal,
  FileInput,
  ArrowRight,
  X,
  Plus,
  Sparkles,
  Zap,
  Globe,
  Rocket
} from "lucide-react";
import SEO from "../components/SEO";
import "./Careers.css";

const Careers = () => {
  const navigate = useNavigate();
  const [popup, setPopup] = useState(null);
  const observerRef = useRef(null);
  const cardRefs = useRef([]);

  useEffect(() => {
    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view-fancy');
        }
      });
    }, { threshold: 0.1 });

    const hiddenElements = document.querySelectorAll('.fancy-animate-scroll');
    hiddenElements.forEach((el) => observerRef.current.observe(el));

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, []);

  const handleMouseMove = (e, index) => {
    const card = cardRefs.current[index];
    if (!card) return;

    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const rotateX = (y - centerY) / 10;
    const rotateY = (centerX - x) / 10;

    card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.05, 1.05, 1.05)`;

    // Shiny effect
    const glare = card.querySelector('.card-glare-effect');
    if (glare) {
      glare.style.background = `radial-gradient(circle at ${x}px ${y}px, rgba(255,255,255,0.3) 0%, transparent 80%)`;
    }
  };

  const handleMouseLeave = (index) => {
    const card = cardRefs.current[index];
    if (!card) return;
    card.style.transform = `perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)`;
    const glare = card.querySelector('.card-glare-effect');
    if (glare) {
      glare.style.background = `transparent`;
    }
  };

  const positions = [
    {
      title: "Business Executive",
      icon: <Briefcase className="animated-icon-svg" size={24} />,
      badge: "Full-Time",
      tags: ["Strategy", "Growth", "Leadership"],
      description:
        "Lead and manage business operations and strategy to drive growth and success. Develop business plans, improve efficiency, identify new opportunities, and collaborate with teams. Strong leadership, analytical skills, and a results-driven mindset are essential.",
    },
    {
      title: "FrontEnd Developer",
      icon: <Code className="animated-icon-svg" size={24} />,
      badge: "Full-Time",
      tags: ["React", "UI/UX", "Tailwind"],
      description:
        "Design and build modern, user-friendly web applications using ReactJS. Collaborate with designers and backend teams to create seamless, high-performance interfaces that deliver exceptional user experiences. Strong skills in JavaScript, HTML, CSS, and a keen eye for detail are essential.",
    },
    {
      title: ".NET Developer",
      icon: <Terminal className="animated-icon-svg" size={24} />,
      badge: "Full-Time",
      tags: ["C#", "ASP.NET", "Microservices"],
      description:
        "Design and develop robust, scalable applications using .NET technologies. Collaborate with cross-functional teams to deliver high-quality, secure, and efficient software solutions. Strong expertise in C#, ASP.NET, and SQL, along with problem-solving skills, are essential.",
    },
    {
      title: "Data Entry Operator",
      icon: <FileInput className="animated-icon-svg" size={24} />,
      badge: "Full-Time",
      tags: ["Accuracy", "Database", "Admin"],
      description:
        "Accurately and efficiently enter, update, and maintain data in company systems and databases. Ensure data integrity, support administrative tasks, and assist in preparing reports. Strong attention to detail, organizational skills, and a high level of accuracy are essential.",
    },
  ];

  const handleMore = (position) => {
    setPopup(position);
  };

  return (
    <div className="ultra-modern-careers-page">
      <SEO
        title="Careers & Job Openings — D Base Solutions Pvt Ltd"
        description="Join D Base Solutions engineering team in Eluru and Vijayawada. Explore open opportunities for React Developers, .NET Engineers, and Data Analysts."
        keywords="D Base Solutions careers, IT jobs Eluru, software engineer jobs Vijayawada, React developer jobs, .NET jobs Andhra Pradesh"
        canonical="https://dbasesolutions.in/careers"
        ogImage="/og-careers.png"
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Careers", url: "/careers" }
        ]}
      />
      {/* Immersive Animated Background */}
      <div className="dynamic-galaxy-bg">
        <div className="nebula-orb orb-1"></div>
        <div className="nebula-orb orb-2"></div>
        <div className="nebula-orb orb-3"></div>
        <div className="floating-particles">
          {[...Array(20)].map((_, i) => (
            <div key={i} className={`particle p${i}`}></div>
          ))}
        </div>
      </div>

      <div className="hero-content-container">
        <header className="main-careers-header fancy-animate-scroll">
          <div className="header-status-badge">
            <Zap size={14} className="pulse-icon" />
            <span>We're actively hiring across all departments</span>
          </div>
          <h1 className="main-careers-title">
            Build the <span className="gradient-text-shimmer">Future</span> With Us
          </h1>
          <p className="main-careers-subtext">
            Join a collective of visionary engineers, designers, and strategists. We aren't just building IT solutions—we're crafting the next digital era.
          </p>

          <div className="header-decoration-icons">
            <Rocket className="floating-header-icon item-1" />
            <Globe className="floating-header-icon item-2" />
            <Sparkles className="floating-header-icon item-3" />
          </div>
        </header>

        <div className="creative-jobs-grid">
          {positions.map((position, index) => (
            <div
              key={index}
              ref={el => cardRefs.current[index] = el}
              className="premium-interactive-card fancy-animate-scroll"
              onMouseMove={(e) => handleMouseMove(e, index)}
              onMouseLeave={() => handleMouseLeave(index)}
            >
              <div className="card-glare-effect"></div>
              <div className="premium-card-inner">
                <div className="top-layout">
                  <div className="icon-wrapper-3d">
                    <div className="icon-main-box">
                      {position.icon}
                    </div>
                  </div>
                  <span className="job-badge-pill">{position.badge}</span>
                </div>

                <div className="mid-layout">
                  <h2 className="premium-job-title">{position.title}</h2>
                  <div className="job-tags-container">
                    {position.tags.map((tag, tIdx) => (
                      <span key={tIdx} className="job-mini-tag">#{tag}</span>
                    ))}
                  </div>
                  <p className="premium-job-excerpt">
                    {position.description.slice(0, 110)}...
                  </p>
                </div>

                <div className="bottom-layout-actions">
                  <button onClick={() => handleMore(position)} className="btn-glass-alt">
                    <Plus size={16} /> Details
                  </button>
                  <button onClick={() => navigate("/form")} className="btn-plasma-primary">
                    Apply <ArrowRight size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Call to Action Section */}
        <div className="careers-footer-cta fancy-animate-scroll">
          <div className="cta-glass-box">
            <h3>Don't see your role?</h3>
            <p>We are always on the lookout for exceptional talent. Reach out to us anyway.</p>
            <button onClick={() => navigate("/contact")} className="cta-link-btn">
              Contact Our HR <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {popup && (
        <div className="ultra-popup-overlay" onClick={() => setPopup(null)}>
          <div className="glass-popup-container" onClick={(e) => e.stopPropagation()}>
            <button className="popup-close-fancy" onClick={() => setPopup(null)}>
              <X size={20} />
            </button>
            <div className="popup-inner-scroll">
              <div className="popup-top-info">
                <div className="popup-large-icon">{popup.icon}</div>
                <h3 className="popup-heading-text">{popup.title}</h3>
                <div className="popup-badges">
                  <span className="p-badge">{popup.badge}</span>
                </div>
              </div>
              <div className="popup-detailed-body">
                <div className="p-section">
                  <h4>About the Role</h4>
                  <p>{popup.description}</p>
                </div>
                <div className="p-section">
                  <h4>Expectations</h4>
                  <ul className="modern-checklist">
                    <li><Sparkles size={14} /> Drive innovation and solve complex problems</li>
                    <li><Sparkles size={14} /> High-level collaboration with product teams</li>
                    <li><Sparkles size={14} /> Attention to detail and code quality</li>
                    <li><Sparkles size={14} /> Passion for learning and emerging tech</li>
                  </ul>
                </div>
              </div>
              <div className="popup-footer-actions">
                <button onClick={() => navigate("/form")} className="apply-btn-full">
                  Submit Application Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Careers;

